import { Expense } from "@finway-group/shared/lib/models"
import { DatePicker } from "antd"
import moment from "moment"
import React from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"

import { NotificationService } from "../../../../../shared/services"
import { patchExpense } from "../../../../../shared/store/actions/expense/expenseActions"
import { isMileageExpense, isPerDiemExpense } from "../../../../../shared/utils/expense.utils"
import { getPopupAnchor } from "../../../../../shared/utils/popup.utils"

interface InvoiceDateFieldProps {
    expense: Expense
    setIsExpenseUpdating?: React.Dispatch<React.SetStateAction<boolean>>
    areInputsDisabled: boolean
    shouldShowInvoiceFields: boolean
    isInvoiceFieldRequired: boolean
    paymentTerm: number | undefined
}

export const InvoiceDateField = ({ expense, setIsExpenseUpdating, areInputsDisabled, shouldShowInvoiceFields, isInvoiceFieldRequired, paymentTerm }: InvoiceDateFieldProps) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const invoiceDate = expense.invoiceDate ? moment(expense.invoiceDate) : undefined
    const invoiceDueDate = expense.invoiceDueDate ? moment(expense.invoiceDueDate) : undefined

    const onInvoiceDateUpdate = async (value: moment.Moment | null) => {
        try {
            setIsExpenseUpdating?.(true)

            const updateData: Record<string, moment.Moment | Date | null> = { invoiceDate: value }
            if (paymentTerm && value) {
                const dueDate = moment(value).add(paymentTerm, "days")
                if (dueDate !== invoiceDueDate) {
                    updateData.invoiceDueDate = dueDate
                }
            }
            if (isMileageExpense(expense) || isPerDiemExpense(expense)) {
                updateData.datePurchased = updateData.invoiceDate
            }
            await patchExpense(expense.id, updateData)(dispatch)
        } catch (err) {
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:request.edit.title"))
        } finally {
            setIsExpenseUpdating?.(false)
        }
    }

    if (shouldShowInvoiceFields) {
        return (
            <li>
                <span className={`w-1/2 title ${isInvoiceFieldRequired && "label-required"}`}>
                    {isMileageExpense(expense) || isPerDiemExpense(expense) ? t("label:internal_invoice_date") : t("label:invoice_date")}
                </span>
                <DatePicker
                    className="w-1/2"
                    value={invoiceDate}
                    format={moment.localeData().longDateFormat("L")}
                    placeholder={t("placeholder:request.date")}
                    onChange={(value) => onInvoiceDateUpdate(value)}
                    getPopupContainer={getPopupAnchor()}
                    disabledDate={isMileageExpense(expense) ? (date: moment.Moment) => date.isAfter(moment(), "day") : () => false}
                    disabled={areInputsDisabled}
                />
            </li>
        )
    }

    if (!expense.invoiceDate) {
        return null
    }

    return (
        <li>
            <span className="title">{isMileageExpense(expense) || isPerDiemExpense(expense) ? t("label:internal_invoice_date") : t("label:invoice_date")}</span>
            <span data-testid="invoiceDateExpenseForm" className="text">
                {moment(expense.invoiceDate).format("ll")}
            </span>
        </li>
    )
}
